import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Tabs,
  Tab,
  IconButton,
  Chip,
  CircularProgress,
  Divider,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import GridOnIcon from "@mui/icons-material/GridOn";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { useHistory } from "react-router-dom";
import { DOMAINS, sourceLabel } from "./fieldCatalog";
import { listReportTemplatesByDomain, deleteReportTemplate } from "../../services/ReportTemplate";
import GenerateReportDialog from "./generate/GenerateReportDialog";
import TemplatePreviewScreen from "./preview/TemplatePreviewScreen";
import { RoleBasedRender } from "../../components/RoleBaseRender";

// Roles that may create/edit/delete templates (generate is available to all who see the page).
const CREATE_ROLES = ["ROLE_ADMIN", "REPORT_CREATE"];

const datePart = (v) => (v ? String(v).split(/[ T]/)[0] : "");

// Reconstruct the working template document a saved row represents.
const toWorkingTemplate = (row) => ({
  ...(row.layout || {}),
  id: row.id,
  name: row.name,
  domain: row.domain,
  visibility: row.visibility,
});

export default function ReportTemplateList() {
  const history = useHistory();
  const [domainIdx, setDomainIdx] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [genTemplate, setGenTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const domain = DOMAINS[domainIdx].key;

  const load = useCallback(async () => {
    setLoading(true);
    const rows = await listReportTemplatesByDomain(domain);
    setTemplates(Array.isArray(rows) ? rows : []);
    setLoading(false);
  }, [domain]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete template "${row.name}"?`)) return;
    const ok = await deleteReportTemplate(row.id);
    if (ok) load();
  };

  return (
    <Box className="page">
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <AssessmentIcon color="info" />
          <Typography variant="h5" fontWeight={700}>
            Report Templates
          </Typography>
        </Box>
        <RoleBasedRender allowedRoles={CREATE_ROLES}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => history.push("/template/report-builder/new")}>
            New Template
          </Button>
        </RoleBasedRender>
      </Box>

      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Design your own Excel report layouts for Sub Orders, Imports and Exports — drag components onto the sheet
        and map data fields, then generate reports.
      </Typography>

      <Paper variant="outlined">
        <Tabs value={domainIdx} onChange={(_, v) => setDomainIdx(v)} sx={{ px: 1 }}>
          {DOMAINS.map((d) => (
            <Tab key={d.key} label={d.label} />
          ))}
        </Tabs>
        <Divider />

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : templates.length === 0 ? (
          <Box sx={{ textAlign: "center", color: "text.secondary", py: 6 }}>
            <Typography>No templates for {DOMAINS[domainIdx].label} yet.</Typography>
            <Typography variant="body2">Click “New Template” to build one.</Typography>
          </Box>
        ) : (
          <Box>
            {templates.map((row) => (
              <Box
                key={row.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  px: 2,
                  py: 1.5,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  "&:last-child": { borderBottom: "none" },
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontWeight={700} noWrap>
                    {row.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {sourceLabel(row.layout && row.layout.source)} · updated {datePart(row.updatedAt) || "—"}
                    {row.createdBy ? ` · by ${row.createdBy}` : ""}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={row.visibility === "SHARED" ? "Shared" : "Private"}
                  color={row.visibility === "SHARED" ? "success" : "default"}
                  variant="outlined"
                />
                <Tooltip title="Preview (full data)">
                  <IconButton onClick={() => setPreviewTemplate(toWorkingTemplate(row))}>
                    <VisibilityIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Generate Excel">
                  <IconButton color="success" onClick={() => setGenTemplate(toWorkingTemplate(row))}>
                    <GridOnIcon />
                  </IconButton>
                </Tooltip>
                <RoleBasedRender allowedRoles={CREATE_ROLES}>
                  <>
                    <Tooltip title="Edit">
                      <IconButton onClick={() => history.push("/template/report-builder/edit/" + row.id)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton color="error" onClick={() => handleDelete(row)}>
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Tooltip>
                  </>
                </RoleBasedRender>
              </Box>
            ))}
          </Box>
        )}
      </Paper>

      {genTemplate && (
        <GenerateReportDialog open={!!genTemplate} onClose={() => setGenTemplate(null)} template={genTemplate} />
      )}

      {previewTemplate && (
        <TemplatePreviewScreen open onClose={() => setPreviewTemplate(null)} template={previewTemplate} />
      )}
    </Box>
  );
}
